import React, { useCallback, useState } from 'react';
import { Button, Flex, Heading, Text, TextField } from '@vibe/core';
import { rejectTimesheet } from '../../api/timesheet-approval.api';
import type { TimesheetSubmission } from '../../types/timesheet-approval.types';

export interface TimesheetRejectDialogProps {
  timesheetId: string;
  onSuccess: (updated: TimesheetSubmission) => void;
  onClose: () => void;
}

/**
 * Dialog to reject a timesheet with a mandatory reason (min 10 chars).
 */
const TimesheetRejectDialog: React.FC<TimesheetRejectDialogProps> = ({
  timesheetId,
  onSuccess,
  onClose,
}) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isValid = reason.trim().length >= 10;

  const handleReject = useCallback(async () => {
    if (!isValid) return;
    setSubmitting(true);
    setError(null);

    try {
      const result = await rejectTimesheet(timesheetId, { reason });
      onSuccess(result);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }, [timesheetId, reason, isValid, onSuccess]);

  return (
    <div className="rp-ts-dialog-overlay" onClick={onClose}>
      <div
        className="rp-ts-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <Heading type={Heading.types.H3}>Reject Timesheet</Heading>

        <div className="rp-ts-dialog__body">
          <Text type={Text.types.TEXT2}>
            Please provide a reason for rejecting this timesheet. The submitter
            will see this reason and can address it before resubmitting.
          </Text>

          <TextField
            title="Rejection reason (required, min 10 characters)"
            placeholder="Explain why the timesheet is being rejected..."
            value={reason}
            onChange={(val: string) => setReason(val)}
            size={TextField.sizes.MEDIUM}
            validation={{
              status:
                reason.length > 0 && !isValid ? 'error' : undefined,
              text:
                reason.length > 0 && !isValid
                  ? `${10 - reason.trim().length} more characters needed`
                  : undefined,
            }}
          />

          {error && (
            <Text type={Text.types.TEXT2} style={{ color: '#e2445c' }}>
              {error}
            </Text>
          )}
        </div>

        <Flex
          gap={Flex.gaps.SMALL}
          justify={Flex.justify.END}
          className="rp-ts-dialog__actions"
        >
          <Button
            kind={Button.kinds.TERTIARY}
            size={Button.sizes.SMALL}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            kind={Button.kinds.PRIMARY}
            size={Button.sizes.SMALL}
            onClick={handleReject}
            loading={submitting}
            disabled={!isValid}
            color={Button.colors.NEGATIVE}
          >
            Reject
          </Button>
        </Flex>
      </div>
    </div>
  );
};

export default TimesheetRejectDialog;
